// Persistent client-side error recorder.
// Captures every uncaught error / rejection / boundary error with full context
// so the exact failing component can be identified from the user's own browser
// (the bug only reproduces in specific profiles). Stored locally; nothing is sent
// anywhere. The user opens /diagnostico and copies the report.

export type ErrorEntry = {
  at: string;
  kind: "onerror" | "unhandledrejection" | "boundary" | "manual";
  name: string;
  message: string;
  stack?: string;
  componentStack?: string;
  route: string;
  url: string;
  breadcrumbs: string[];
  env: Record<string, unknown>;
};

const KEY = "futbux:error-log";
const MAX = 20;
const MAX_CRUMBS = 25;

let breadcrumbs: string[] = [];
let installed = false;

function safeParse(raw: string | null): ErrorEntry[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? (v as ErrorEntry[]) : [];
  } catch {
    return [];
  }
}

export function readErrorLog(): ErrorEntry[] {
  if (typeof window === "undefined") return [];
  try {
    return safeParse(window.localStorage.getItem(KEY));
  } catch {
    return [];
  }
}

export function clearErrorLog() {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* storage blocked */
  }
}

export function addBreadcrumb(action: string) {
  if (typeof window === "undefined") return;
  const stamp = new Date().toLocaleTimeString("pt-BR");
  breadcrumbs.push(`${stamp} · ${action}`);
  if (breadcrumbs.length > MAX_CRUMBS) breadcrumbs = breadcrumbs.slice(-MAX_CRUMBS);
}

export function collectEnvironment(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  const nav = navigator as Navigator & {
    userAgentData?: { brands?: { brand: string; version: string }[]; platform?: string };
    deviceMemory?: number;
  };
  let storageOk = true;
  try {
    window.localStorage.setItem("futbux:probe", "1");
    window.localStorage.removeItem("futbux:probe");
  } catch {
    storageOk = false;
  }
  const html = document.documentElement;
  return {
    appVersion: import.meta.env["VITE_APP_VERSION"] ?? "dev",
    url: window.location.href,
    route: window.location.pathname,
    userAgent: nav.userAgent,
    brands: nav.userAgentData?.brands?.map((b) => `${b.brand} ${b.version}`).join(", "),
    platform: nav.userAgentData?.platform,
    language: nav.language,
    languages: nav.languages?.join(", "),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: `${window.screen.width}x${window.screen.height} @${window.devicePixelRatio}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    online: nav.onLine,
    cookiesEnabled: nav.cookieEnabled,
    localStorageOk: storageOk,
    translated:
      html.classList.contains("translated-ltr") ||
      html.classList.contains("translated-rtl") ||
      !!document.querySelector("font[_msttexthash]") ||
      !!document.querySelector(".goog-te-banner-frame"),
    // Extensions commonly inject elements into <body>; listing unknown top-level
    // tags helps spot Grammarly, password managers, ad-blockers, etc.
    injectedBodyNodes: Array.from(document.body.children)
      .map((el) => el.tagName.toLowerCase() + (el.id ? `#${el.id}` : ""))
      .filter((t) => !["script", "div", "nextjs-portal"].includes(t.split("#")[0]!))
      .slice(0, 15)
      .join(", "),
  };
}

export function recordError(input: {
  kind: ErrorEntry["kind"];
  error: unknown;
  componentStack?: string;
}) {
  if (typeof window === "undefined") return;
  const err = input.error;
  const entry: ErrorEntry = {
    at: new Date().toISOString(),
    kind: input.kind,
    name: err instanceof Error ? err.name : typeof err,
    message: err instanceof Error ? err.message : String(err),
    stack: err instanceof Error ? err.stack : undefined,
    componentStack: input.componentStack,
    route: window.location.pathname,
    url: window.location.href,
    breadcrumbs: [...breadcrumbs],
    env: collectEnvironment(),
  };
  try {
    const list = readErrorLog();
    list.unshift(entry);
    window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    /* storage blocked — console still has it */
  }
  console.error("[FutBux] erro registrado", entry);
}

export function formatReport(): string {
  const entries = readErrorLog();
  const env = collectEnvironment();
  const lines: string[] = ["=== RELATÓRIO DE DIAGNÓSTICO FUTBUX ==="];
  lines.push(`Gerado em: ${new Date().toISOString()}`);
  lines.push("");
  lines.push("--- Ambiente ---");
  Object.entries(env).forEach(([k, v]) => lines.push(`${k}: ${String(v)}`));
  lines.push("");
  lines.push(`--- Erros registrados (${entries.length}) ---`);
  entries.forEach((e, i) => {
    lines.push("");
    lines.push(`#${i + 1} [${e.kind}] ${e.at} — ${e.route}`);
    lines.push(`${e.name}: ${e.message}`);
    if (e.breadcrumbs.length) {
      lines.push("Últimas ações:");
      e.breadcrumbs.forEach((b) => lines.push(`  ${b}`));
    }
    if (e.componentStack) {
      lines.push("Componentes:");
      lines.push(e.componentStack.trim());
    }
    if (e.stack) {
      lines.push("Stack:");
      lines.push(e.stack.trim());
    }
  });
  return lines.join("\n");
}

export function installErrorLog() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    recordError({ kind: "onerror", error: (event as ErrorEvent).error ?? event.message });
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordError({ kind: "unhandledrejection", error: (event as PromiseRejectionEvent).reason });
  });

  // Breadcrumbs: clicks on interactive elements + route changes.
  document.addEventListener(
    "click",
    (event) => {
      const el = (event.target as HTMLElement | null)?.closest(
        "button, a, [role='button'], [role='tab'], [role='option'], input, select",
      );
      if (!el) return;
      const label =
        el.getAttribute("aria-label") ||
        (el.textContent || "").trim().slice(0, 40) ||
        el.getAttribute("name") ||
        el.tagName.toLowerCase();
      addBreadcrumb(`clique: ${label}`);
    },
    true,
  );

  addBreadcrumb(`abriu ${window.location.pathname}`);
}
